import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    neon: Palette['primary'];
  }
  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
    neon?: PaletteOptions['primary'];
  }
}

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { 
      main: '#3b82f6', 
      light: '#60a5fa', 
      dark: '#2563eb', 
      contrastText: '#ffffff' 
    }, // Vibrant blue
    secondary: { 
      main: '#8b5cf6', 
      light: '#a78bfa', 
      dark: '#7c3aed', 
      contrastText: '#ffffff' 
    }, // Purple
    text: { primary: '#1e293b', secondary: '#64748b' },
    neutral: { main: '#f1f5f9', light: '#f8fafc', dark: '#e2e8f0', contrastText: '#475569' },
    neon: { 
      main: '#00ff88', 
      light: '#4dffaa', 
      dark: '#00cc6a', 
      contrastText: '#000000' 
    }, // Neon green for accents
  },
  typography: {
    fontFamily: '"Kievit", "Roboto", sans-serif',
    h1: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '3rem', lineHeight: 1.2, fontStyle: 'normal' },
    h2: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '2.5rem', lineHeight: 1.3, fontStyle: 'normal' },
    h3: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '2rem', lineHeight: 1.4, fontStyle: 'normal' },
    h4: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '1.5rem', lineHeight: 1.4, fontStyle: 'normal' },
    h5: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '1.25rem', lineHeight: 1.5, fontStyle: 'normal' },
    h6: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '1rem', lineHeight: 1.5, fontStyle: 'normal' },
    body1: { fontFamily: '"Kievit", "Roboto", sans-serif', fontSize: '1rem', lineHeight: 1.6, fontWeight: 400 },
    body2: { fontFamily: '"Kievit", "Roboto", sans-serif', fontSize: '0.875rem', lineHeight: 1.6, fontWeight: 400 },
    button: { fontFamily: '"Kievit", "Roboto", sans-serif', fontWeight: 500, textTransform: 'none' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '10px 20px',
          fontSize: '0.95rem',
          fontWeight: 500,
          letterSpacing: '0.5px',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { 
      main: '#60a5fa', 
      light: '#93c5fd', 
      dark: '#3b82f6', 
      contrastText: '#0f172a' 
    }, // Lighter blue for dark mode
    secondary: { 
      main: '#a78bfa', 
      light: '#c4b5fd', 
      dark: '#8b5cf6', 
      contrastText: '#0f172a' 
    }, // Lighter purple for dark mode
    background: { default: '#0f172a', paper: '#1e293b' },
    text: { primary: '#f1f5f9', secondary: '#cbd5e1' },
    neutral: { main: '#334155', light: '#475569', dark: '#1e293b', contrastText: '#f1f5f9' },
    neon: { 
      main: '#00ff88', 
      light: '#4dffaa', 
      dark: '#00cc6a', 
      contrastText: '#000000' 
    }, // Neon green for accents
  },
  typography: {
    fontFamily: '"Kievit", "Roboto", sans-serif',
    h1: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '3rem', lineHeight: 1.2, fontStyle: 'normal' },
    h2: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '2.5rem', lineHeight: 1.3, fontStyle: 'normal' },
    h3: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '2rem', lineHeight: 1.4, fontStyle: 'normal' },
    h4: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '1.5rem', lineHeight: 1.4, fontStyle: 'normal' },
    h5: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '1.25rem', lineHeight: 1.5, fontStyle: 'normal' },
    h6: { fontFamily: '"Charter", "Georgia", serif', fontWeight: 400, fontSize: '1rem', lineHeight: 1.5, fontStyle: 'normal' },
    body1: { fontFamily: '"Kievit", "Roboto", sans-serif', fontSize: '1rem', lineHeight: 1.6, fontWeight: 400 },
    body2: { fontFamily: '"Kievit", "Roboto", sans-serif', fontSize: '0.875rem', lineHeight: 1.6, fontWeight: 400 },
    button: { fontFamily: '"Kievit", "Roboto", sans-serif', fontWeight: 500, textTransform: 'none' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '10px 20px',
          fontSize: '0.95rem',
          fontWeight: 500,
          letterSpacing: '0.5px',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(96, 165, 250, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(96, 165, 250, 0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

export { lightTheme, darkTheme }; 